interface GridCellTooltipParams {
  colDef?: object | null;
  value?: unknown;
  valueFormatted?: string | null;
}

/**
 * Whether a column paints its cells through its own renderer component. Those cells own their
 * markup (pills, dropdowns, icons), so the grid's truncation check cannot see their text.
 * @param colDef - the column definition from the tooltip params.
 * @returns true when the column declares a cell renderer or renderer selector.
 */
function hasCustomCellRenderer(colDef: object | null | undefined): boolean {
  if (!colDef) return false;
  const rendererKeys = ['cellRenderer', 'cellRendererSelector'] as const;
  return rendererKeys.some((key) => key in colDef && (colDef as Record<string, unknown>)[key] != null);
}

/**
 * Default `tooltipValueGetter` for the shared AG Grid wrappers: the cell's displayed text for
 * plain-text columns, paired with `tooltipShowMode: 'whenTruncated'` so it only appears when the
 * text is cut off. Columns with a custom renderer and non-text values get no tooltip.
 * @param params - AG Grid tooltip params.
 * @returns the full cell text, or undefined when no tooltip applies.
 */
export function getTruncatedCellTooltip({ colDef, value, valueFormatted }: GridCellTooltipParams): string | undefined {
  if (hasCustomCellRenderer(colDef)) return undefined;
  const text = valueFormatted ?? value;
  if (typeof text === 'number') return String(text);
  if (typeof text === 'string' && text.trim() !== '') return text;
  return undefined;
}

export const GRID_TOOLTIP_SHOW_DELAY_MS = 300;
